import { useState, useMemo, useCallback } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { Box, Container, Flex, Text, Heading, Button, IconButton } from "@chakra-ui/react"
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react"
import type { GameCourseProgress } from "@zrt-ai-office/types/course"
import { getGameCourse } from "@/lib/academy/game-mock-data"
import { GameStepIndicator } from "@/src/components/academy/game/game-step-indicator"
import { GameStepContent } from "@/src/components/academy/game/game-step-content"
import { GameChallenge } from "@/src/components/academy/game/game-challenge"
import { GameJudge } from "@/src/components/academy/game/game-judge"

/** Read game progress from localStorage */
function loadProgress(courseId: number): GameCourseProgress {
  try {
    const raw = localStorage.getItem(`game-progress-${courseId}`)
    if (raw) return JSON.parse(raw) as GameCourseProgress
  } catch {
    // ignore parse errors
  }
  return { courseId, chapters: {} }
}

/** Save game progress to localStorage */
function saveProgress(progress: GameCourseProgress) {
  localStorage.setItem(
    `game-progress-${progress.courseId}`,
    JSON.stringify(progress)
  )
}

export function GameLevelPage() {
  const { courseId, chapterId } = useParams<{
    courseId: string
    chapterId: string
  }>()
  const navigate = useNavigate()

  const numCourseId = Number(courseId)
  const numChapterId = Number(chapterId)

  /* ── Load course and chapter data ── */
  const course = useMemo(() => getGameCourse(numCourseId), [numCourseId])

  const sortedChapters = useMemo(() => {
    if (!course) return []
    return [...course.chapters].sort((a, b) => a.sortOrder - b.sortOrder)
  }, [course])

  const chapterIndex = useMemo(
    () => sortedChapters.findIndex((ch) => ch.id === numChapterId),
    [sortedChapters, numChapterId]
  )

  const chapter = chapterIndex >= 0 ? sortedChapters[chapterIndex] : null

  const isLastLevel = chapterIndex === sortedChapters.length - 1

  const nextChapter =
    chapterIndex >= 0 && chapterIndex < sortedChapters.length - 1
      ? sortedChapters[chapterIndex + 1]
      : null

  /* ── Step navigation state ── */
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [showSuccess, setShowSuccess] = useState(false)

  const steps = useMemo(() => {
    if (!chapter) return []
    return [...chapter.steps].sort((a, b) => a.sortOrder - b.sortOrder)
  }, [chapter])

  const currentStep = steps[currentStepIndex] ?? null
  const isLastStep = currentStepIndex === steps.length - 1
  const isFirstStep = currentStepIndex === 0
  const isChallenge = currentStep?.contentType === "challenge"

  /* ── Check accessibility via progress ── */
  const progress = useMemo(() => loadProgress(numCourseId), [numCourseId])

  const isAccessible = useMemo(() => {
    if (!chapter) return false
    if (chapter.status === "available" || chapter.status === "completed") return true
    // Check if previous chapter is completed in localStorage
    if (chapterIndex <= 0) return true
    const prevChapter = sortedChapters[chapterIndex - 1]
    return !!progress.chapters[prevChapter.id]?.completed
  }, [chapter, chapterIndex, sortedChapters, progress])

  /* ── Handlers ── */
  const handlePrevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1)
    }
  }, [currentStepIndex])

  const handleNextStep = useCallback(() => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1)
    }
  }, [currentStepIndex, steps.length])

  const handleStepClick = useCallback((index: number) => {
    setCurrentStepIndex(index)
  }, [])

  const handleChallengeComplete = useCallback(() => {
    // Save progress
    const updated: GameCourseProgress = {
      ...progress,
      courseId: numCourseId,
      chapters: {
        ...progress.chapters,
        [numChapterId]: { completed: true, stars: 3 },
      },
    }
    saveProgress(updated)
    setShowSuccess(true)
  }, [progress, numCourseId, numChapterId])

  const handleNext = useCallback(() => {
    setShowSuccess(false)
    if (nextChapter) {
      navigate(`/academy/game/${numCourseId}/${nextChapter.id}`)
      setCurrentStepIndex(0)
    } else {
      // Last level - go back to map
      navigate(`/academy/game/${numCourseId}`)
    }
  }, [nextChapter, numCourseId, navigate])

  const handleBackToMap = useCallback(() => {
    setShowSuccess(false)
    navigate(`/academy/game/${numCourseId}`)
  }, [numCourseId, navigate])

  /* ── Error states ── */
  if (!course || !chapter) {
    return (
      <Container maxW="4xl" py="20" textAlign="center">
        <Text color="gray.400">找不到该关卡</Text>
        <Link to="/academy">
          <Button mt="4" variant="ghost" color="emerald.600">
            返回课程列表
          </Button>
        </Link>
      </Container>
    )
  }

  if (!isAccessible) {
    return (
      <Container maxW="4xl" py="20" textAlign="center">
        <Text fontSize="4xl" mb="4">🔒</Text>
        <Heading size="lg" mb="2" color="slate.700">
          关卡未解锁
        </Heading>
        <Text color="gray.400" mb="4">
          请先完成前一个关卡
        </Text>
        <Link to={`/academy/game/${numCourseId}`}>
          <Button variant="ghost" color="emerald.600">
            返回关卡地图
          </Button>
        </Link>
      </Container>
    )
  }

  return (
    <Box minH="100vh" bg="gray.50" pt="14">
      <Container maxW="3xl" py="6" px="4">
        {/* ── Top bar: back link + title ── */}
        <Flex align="center" gap="3" mb="5">
          <Link to={`/academy/game/${numCourseId}`}>
            <IconButton
              aria-label="返回关卡地图"
              variant="ghost"
              size="sm"
              color="gray.500"
              _hover={{ color: "gray.700", bg: "gray.100" }}
              borderRadius="lg"
            >
              <ArrowLeft size={18} />
            </IconButton>
          </Link>
          <Box>
            <Text fontSize="xs" color="gray.400">
              {course.title}
            </Text>
            <Heading as="h1" size="md" color="slate.900">
              关卡 {chapterIndex + 1}：{chapter.title}
            </Heading>
          </Box>
        </Flex>

        {/* ── Step indicator ── */}
        <GameStepIndicator
          total={steps.length}
          current={currentStepIndex}
          onStepClick={handleStepClick}
        />

        {/* ── Main content card ── */}
        <Box
          bg="white"
          borderRadius="2xl"
          border="1px solid"
          borderColor="gray.200"
          p={{ base: "5", md: "8" }}
          mt="4"
          mb="24"
          shadow="sm"
        >
          {currentStep && currentStep.contentType === "md" && (
            <GameStepContent
              step={currentStep}
              stepIndex={currentStepIndex}
              totalSteps={steps.length}
            />
          )}

          {currentStep && currentStep.contentType === "challenge" && (
            <GameChallenge
              step={currentStep}
              onComplete={handleChallengeComplete}
            />
          )}
        </Box>
      </Container>

      {/* ── Fixed bottom navigation bar ── */}
      <Box
        position="fixed"
        bottom="0"
        left="0"
        right="0"
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        py="3"
        px="4"
        zIndex={30}
        boxShadow="0 -2px 10px rgba(0,0,0,0.05)"
      >
        <Container maxW="3xl">
          <Flex justify="space-between" align="center">
            <Button
              onClick={handlePrevStep}
              disabled={isFirstStep}
              variant="outline"
              size="lg"
              px="6"
              borderRadius="xl"
              style={{
                borderColor: isFirstStep ? "#e2e8f0" : "#94a3b8",
                color: isFirstStep ? "#cbd5e1" : "#475569",
                opacity: isFirstStep ? 0.5 : 1,
              }}
            >
              <Flex align="center" gap="1.5">
                <ChevronLeft size={18} />
                上一步
              </Flex>
            </Button>

            {/* Step counter in the middle */}
            <Text fontSize="sm" color="gray.400" fontWeight="medium">
              {currentStepIndex + 1} / {steps.length}
            </Text>

            {/* Hide "next" button if the last step is a challenge */}
            {!(isLastStep && isChallenge) ? (
              <Button
                onClick={handleNextStep}
                disabled={isLastStep}
                size="lg"
                px="6"
                borderRadius="xl"
                fontWeight="semibold"
                style={{
                  backgroundColor: isLastStep ? "#e2e8f0" : "#10b981",
                  color: isLastStep ? "#94a3b8" : "#ffffff",
                  opacity: isLastStep ? 0.5 : 1,
                }}
              >
                <Flex align="center" gap="1.5">
                  下一步
                  <ChevronRight size={18} />
                </Flex>
              </Button>
            ) : (
              <Button
                onClick={handlePrevStep}
                variant="outline"
                size="lg"
                px="6"
                borderRadius="xl"
                style={{
                  borderColor: "#94a3b8",
                  color: "#475569",
                }}
              >
                <Flex align="center" gap="1.5">
                  <ChevronLeft size={18} />
                  回看教学
                </Flex>
              </Button>
            )}
          </Flex>
        </Container>
      </Box>

      {/* ── Success overlay ── */}
      <GameJudge
        visible={showSuccess}
        stars={3}
        onNext={handleNext}
        onBackToMap={handleBackToMap}
        isLastLevel={isLastLevel}
      />
    </Box>
  )
}
